import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import {
  SheetBody,
  SheetFooter,
  SheetForm,
  SheetHeader,
  SheetTitle,
} from "@hortis/ui/sheet";
import { R } from "@mobily/ts-belt";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { pipe } from "fp-ts/function";
import { namedOperations } from "generated/graphql";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { BaseAccessionDataSchemaZod } from "src/features/create-accession/components/accession-data-form/schema";
import { accessionNumberErrorMessages } from "src/features/create-accession/components/schema";

import { toast } from "@hortis/ui/sonner";
import { useQueryClient } from "@tanstack/react-query";
import { makeValidationReq } from "src/features/create-accession/components/validate-accession-number/make-validation-req";
import {
  mapIdentificationDetails,
  mapMaterialReceivedOptionToCode,
  mapNotesData,
} from "src/features/create-accession/utils/process-form-data";
import { type Nullable } from "src/types/nullable";
import { parseUtcDate } from "src/utils/dates/parse-utc-date";
import { cleanFormData } from "src/utils/forms";
import { mapTagsToIds } from "src/utils/forms/map-tags-to-ids";
import { mapTaxonOptionToId } from "src/utils/forms/map-taxon-option-to-id";
import { useGqlMutation } from "src/utils/hooks/graphql";
import {
  type IdentificationQualifier,
  type IdentificationVerificationStatus,
} from "src/utils/label-maps/identification";
import { type DeepNullable } from "ts-essentials";
import { match } from "ts-pattern";
import { z } from "zod";
import {
  type AccessionFieldsFragment,
  AccessionNumberFormat,
} from "../../../../../../../generated/graphql";
import { AccessionDataFields } from "../../../../../create-accession/components/accession-data-form/accession-data-fields";
import { updateAccessionDataDocument } from "../../../graphql";
import type { BaseEditStageProps } from "../types";

const UpdateAccessionSchema = BaseAccessionDataSchemaZod.superRefine(
  (updates, ctx) => {
    match(updates.accessionNumberFormat)
      .with(AccessionNumberFormat.PrefixSuffix, () => {
        const path = ["accessionNumberSuffix"];
        if (updates.accessionNumberSuffix == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_type,
            message:
              "A valid Suffix is required when using the standard accession number format",
            path,
            expected: "string",
            received: "null",
          });
        }
      })
      .with(AccessionNumberFormat.Exogenous, () => {
        const path = ["exogenousAccessionNumber"];
        const { exogenousAccessionNumber } = updates;

        if (exogenousAccessionNumber == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_type,
            message:
              "A valid string must be provided when using the custom accession number format",
            path,
            expected: "string",
            received: "null",
          });
        }

        if (
          exogenousAccessionNumber != null &&
          exogenousAccessionNumber.length < 3
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_type,
            message: "Custom accession number must be at least 3 digits long",
            path,
            expected: "string",
            received: "null",
          });
        }
      })
      .with(undefined, () => null)
      .with(null, () => null)
      .exhaustive();
  },
);

type UpdateAccesionSchema = z.TypeOf<typeof UpdateAccessionSchema>;

export const extractAccessionDataFormFields = ({
  identifiedBy,
  dateIdentified,
  identificationVerificationStatus,
  identificationQualifier,
  identificationRemarks,
  ...accession
}: AccessionFieldsFragment): Nullable<DeepNullable<UpdateAccesionSchema>> => ({
  accessionDate:
    accession.accessionDate == null
      ? null
      : parseUtcDate(accession.accessionDate),
  accessionNumberFormat: accession.accessionNumberFormat,
  exogenousAccessionNumber: accession.exogenousAccessionNumber,
  accessionNumberSuffix: accession.accessionNumberSuffix,
  provenanceCategoryCode: accession.provenanceCategoryCode,
  taxonOption: accession.taxon ?? null,
  materialTypeId: accession.materialType ?? null,
  donor: accession.donor ?? null,
  notes: accession.notesData ?? null,
  tags:
    accession.tagsConnection?.nodes.map(({ id, name }) => ({ id, name })) ?? [],
  ipenNumber: accession.ipenNumber ?? null,
  identificationDetails:
    identifiedBy != null ||
    dateIdentified != null ||
    identificationVerificationStatus != null ||
    identificationQualifier != null ||
    identificationRemarks != null
      ? {
          identifiedBy: identifiedBy ?? null,
          dateIdentified:
            dateIdentified == null ? null : parseUtcDate(dateIdentified),
          identificationVerificationStatus:
            identificationVerificationStatus as IdentificationVerificationStatus,
          identificationQualifier:
            identificationQualifier as IdentificationQualifier,
          identificationRemarks: identificationRemarks ?? null,
        }
      : null,
});

export const EditAccessionData = ({
  open,
  accession,
  requestClose,
  refetch,
}: BaseEditStageProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const params = matchRoute({
    // eslint-disable-next-line sonarjs/no-duplicate-string
    to: "/site/$siteSlug/accession/$accessionNumber",
  });

  const isAccessionPage = params !== false;

  const updateAccession = useGqlMutation({
    mutation: updateAccessionDataDocument,
    onError: () => {
      toast.error("Failed to update accession");
    },
    onSuccess: (res) => {
      if (res.updateAccessionData?.__typename === "GenericError") {
        throw new Error("Failed to update accession");
      }
      toast.success("Accession data updated");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetAccessions],
      });
      refetch();
      requestClose();

      if (
        isAccessionPage &&
        (formMethods.formState.dirtyFields.accessionNumberSuffix === true ||
          formMethods.formState.dirtyFields.exogenousAccessionNumber === true ||
          formMethods.formState.dirtyFields.accessionDate === true)
      ) {
        if (res.updateAccessionData?.accessionNumber == null) {
          void navigate({
            from: "/site/$siteSlug/accession/$accessionNumber",
            to: "/site/$siteSlug/collection",
            search: {
              tab: "accessions",
            },
            replace: true,
          });
        } else {
          void navigate({
            from: "/site/$siteSlug/accession/$accessionNumber",
            to: "/site/$siteSlug/accession/$accessionNumber",
            params: {
              accessionNumber: res.updateAccessionData.accessionNumber,
            },
            replace: true,
          });
        }
      }
    },
  });
  const formMethods = useForm<Nullable<DeepNullable<UpdateAccesionSchema>>>({
    defaultValues: extractAccessionDataFormFields(accession),
    criteriaMode: "all",
    resolver: zodResolver(UpdateAccessionSchema),
  });

  useEffect(() => {
    formMethods.reset(extractAccessionDataFormFields(accession));
  }, [open, formMethods, accession]);

  const onSubmit = useMemo(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    () =>
      formMethods.handleSubmit(async (data) => {
        const accessionNumberPrefix = data.accessionDate?.getFullYear();
        let accessionNumberError = false;

        if (
          data.accessionNumberFormat === AccessionNumberFormat.PrefixSuffix &&
          accession.accessionNumberSuffix !== data.accessionNumberSuffix
        ) {
          R.match(
            await makeValidationReq({
              accessionNumberPrefix,
              accessionNumberSuffix: data.accessionNumberSuffix ?? undefined,
            }),
            (errors) => {
              if (errors.length > 0) {
                formMethods.setError("accessionNumberSuffix", {
                  types: Object.fromEntries(
                    errors.map((e) => [e, accessionNumberErrorMessages[e]]),
                  ),
                });
                accessionNumberError = true;
              }
            },
            () => {
              formMethods.setError("accessionNumberSuffix", {
                message: "Unable to validate accession number",
              });
              accessionNumberError = true;
            },
          );
        }

        if (
          data.accessionNumberFormat === AccessionNumberFormat.Exogenous &&
          accession.exogenousAccessionNumber !== data.exogenousAccessionNumber
        ) {
          R.match(
            await makeValidationReq({
              exogenousAccessionNumber:
                data.exogenousAccessionNumber ?? undefined,
            }),
            (errors) => {
              if (errors.length > 0) {
                formMethods.setError("exogenousAccessionNumber", {
                  types: Object.fromEntries(
                    errors.map((e) => [e, accessionNumberErrorMessages[e]]),
                  ),
                });
                accessionNumberError = true;
              }
            },
            () => {
              formMethods.setError("exogenousAccessionNumber", {
                message: "Unable to validate accession number",
              });
              accessionNumberError = true;
            },
          );
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (accessionNumberError) {
          return;
        }

        // Required to trigger the accession number auto-generation on the api, same as mapFlexibleAccessionInputs
        if (data.accessionNumberFormat === null) {
          data.accessionNumberFormat = AccessionNumberFormat.PrefixSuffix;
        }

        await updateAccession.mutateAsync({
          accessionId: accession.id,
          updates: pipe(
            data as UpdateAccesionSchema,
            (v) =>
              formMethods.formState.dirtyFields.accessionDate === true
                ? { ...v, accessionNumberPrefix }
                : v,
            mapNotesData,
            mapTaxonOptionToId,
            mapMaterialReceivedOptionToCode,
            mapTagsToIds,
            mapIdentificationDetails,
            cleanFormData,
          ),
        });
      }),
    [
      formMethods,
      accession.accessionNumberSuffix,
      accession.exogenousAccessionNumber,
      accession.id,
      updateAccession,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <SheetForm onSubmit={onSubmit}>
        <SheetHeader>
          <SheetTitle>Details</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <AccessionDataFields />
        </SheetBody>
        <SheetFooter>
          <Button testId="cancel-button" onClick={requestClose}>
            Cancel
          </Button>
          <Button
            testId="confirm-button"
            loading={formMethods.formState.isSubmitting}
            type="submit"
            variant="primary"
          >
            Save
          </Button>
        </SheetFooter>
      </SheetForm>
    </FormProvider>
  );
};
